import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Typography, Paper, Box, Grid, Card, CardContent, Chip, CircularProgress, Alert, Button } from '@mui/material';
import { People as PeopleIcon, Description as TemplateIcon, Email as EmailIcon, Assessment as ReportsIcon } from '@mui/icons-material';
import { ADMIN_VIEW_RENDERERS } from '../navigation/portalPageRegistry';

function AdminDashboard({
    selectedView,
    setSelectedView,
    escalationsAvailability = null,
    escalationsAvailabilityLoaded = false,
    onEscalationsAvailabilityChange
}) {
    const [dashboardStats, setDashboardStats] = useState(null);
    const [userActivitySummary, setUserActivitySummary] = useState(null);
    const [recentActivityRows, setRecentActivityRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState('');
    const [showMoreRecentActivity, setShowMoreRecentActivity] = useState(false);
    const hasDashboardStatsRef = useRef(false);

    // Load dashboard statistics
    const loadDashboardStats = useCallback(async ({ initial = false } = {}) => {
        if (initial && !hasDashboardStatsRef.current) {
            setLoading(true);
        } else {
            setRefreshing(true);
        }

        try {
            const [statsResult, activityResult, recentResult] = await Promise.allSettled([
                fetch('/api/dashboard/stats').then((response) => response.json()),
                fetch('/api/users/activity-summary').then((response) => response.json()),
                fetch('/api/logs/offers-paginated?page=1&limit=15&sort=sentAt_desc').then((response) => response.json())
            ]);

            if (statsResult.status === 'fulfilled' && statsResult.value.success) {
                setDashboardStats(statsResult.value.stats);
                hasDashboardStatsRef.current = true;
                setError('');
            } else {
                setError(statsResult.value?.error || 'Failed to load statistics');
            }

            if (activityResult.status === 'fulfilled' && activityResult.value?.summary) {
                setUserActivitySummary(activityResult.value.summary);
            }

            if (recentResult.status === 'fulfilled' && Array.isArray(recentResult.value?.rows)) {
                setRecentActivityRows(recentResult.value.rows.map((offer) => ({
                    user: offer.agentName || `User ${offer.agentId}`,
                    action: `sent an email to ${offer.customerFullName || 'customer'}`,
                    timestamp: offer.sentAt,
                    sourceLabel: offer.displaySourceLabel || offer.sourceLabel || offer.templateName || 'Template'
                })));
            }
        } catch (err) {
            setError('Failed to connect to server');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    // Load stats when dashboard is selected
    useEffect(() => {
        if (selectedView === 'dashboard') {
            loadDashboardStats({ initial: true });
            // Refresh every 30 seconds
            const interval = setInterval(loadDashboardStats, 30000);
            return () => clearInterval(interval);
        }
    }, [selectedView, loadDashboardStats]);

    const formatDashboardTime = (value) => {
        if (!value) return 'N/A';

        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return value;

        return date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            timeZone: 'America/Vancouver'
        });
    };

    const formatActivityTimestamp = (value) => {
        if (!value) return '';

        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return value;

        return date.toLocaleString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            timeZone: 'America/Vancouver'
        });
    };

    const renderView = () => {
        const recentActivity = recentActivityRows.length > 0
            ? recentActivityRows
            : (dashboardStats?.recentActivity || []);
        const recentActivityLimit = showMoreRecentActivity ? 15 : 5;
        const agentsSentToday = dashboardStats?.userActivity?.activeAgents || 0;
        const agentsSentTodayColor = agentsSentToday > 0 ? 'success' : 'info';

        if (selectedView === 'dashboard') {
            return (
                    <Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                            <Typography variant="h4" gutterBottom>
                                📊 Admin Dashboard
                            </Typography>
                            <Chip 
                                label={`Last updated: ${dashboardStats?.lastUpdated || 'Loading...'}`}
                                color="info"
                                size="small"
                            />
                            {refreshing && (
                                <Chip
                                    label="Refreshing..."
                                    color="default"
                                    size="small"
                                    variant="outlined"
                                    sx={{ ml: 1 }}
                                />
                            )}
                        </Box>

                        {error && (
                            <Alert severity="error" sx={{ mb: 3 }}>
                                {error}
                            </Alert>
                        )}

                        {loading && !dashboardStats ? (
                            <Box display="flex" justifyContent="center" py={4}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <Grid container spacing={3}>
                                {/* System Overview */}
                                <Grid item xs={12}>
                                    <Typography variant="h6" gutterBottom color="primary">
                                        System Overview
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Card>
                                        <CardContent>
                                            <Box display="flex" alignItems="center" justifyContent="between">
                                                <Box>
                                                    <Typography variant="h4" color="primary">
                                                        {dashboardStats?.users?.total || 0}
                                                    </Typography>
                                                    <Typography variant="body2" color="textSecondary">
                                                        Total Users
                                                    </Typography>
                                                </Box>
                                                <PeopleIcon color="primary" sx={{ fontSize: 40, ml: 2 }} />
                                            </Box>
                                            <Box mt={1}>
                                                <Chip 
                                                    label={`${dashboardStats?.users?.active || 0} active`}
                                                    color="success"
                                                    size="small"
                                                />
                                                <Chip 
                                                    label={`${dashboardStats?.users?.admins || 0} admins`}
                                                    color="info"
                                                    size="small"
                                                    sx={{ ml: 1 }}
                                                />
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                <Grid item xs={12} sm={6} md={3}>
                                    <Card>
                                        <CardContent>
                                            <Box display="flex" alignItems="center" justifyContent="between">
                                                <Box>
                                                    <Typography variant="h4" color="secondary">
                                                        {dashboardStats?.emails?.today || 0}
                                                    </Typography>
                                                    <Typography variant="body2" color="textSecondary">
                                                        Emails Today
                                                    </Typography>
                                                </Box>
                                                <EmailIcon color="secondary" sx={{ fontSize: 40, ml: 2 }} />
                                            </Box>
                                            <Box mt={1}>
                                                <Chip 
                                                    label={`${dashboardStats?.emails?.thisWeek || 0} this week`}
                                                    color="secondary"
                                                    size="small"
                                                />
                                                <Chip
                                                    label={`Last sent: ${
                                                        dashboardStats?.emails?.today > 0
                                                            ? formatDashboardTime(dashboardStats?.userActivity?.lastActivity)
                                                            : 'N/A'
                                                    }`}
                                                    color="default"
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ ml: 1, mt: { xs: 1, sm: 0 } }}
                                                />
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                <Grid item xs={12} sm={6} md={3}>
                                    <Card>
                                        <CardContent>
                                            <Box display="flex" alignItems="center" justifyContent="between">
                                                <Box>
                                                    <Typography variant="h4" color="warning.main">
                                                        {dashboardStats?.templates?.total || 0}
                                                    </Typography>
                                                    <Typography variant="body2" color="textSecondary">
                                                        Templates
                                                    </Typography>
                                                </Box>
                                                <TemplateIcon color="warning" sx={{ fontSize: 40, ml: 2 }} />
                                            </Box>
                                            <Box mt={1}>
                                                <Chip 
                                                    label={`${dashboardStats?.templates?.total || 0} stored`}
                                                    color="warning"
                                                    size="small"
                                                />
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                <Grid item xs={12} sm={6} md={3}>
                                    <Card>
                                        <CardContent>
                                            <Box display="flex" alignItems="center" justifyContent="between">
                                                <Box>
                                                    <Typography variant="h6" color="info.main">
                                                        Call Gap Review
                                                    </Typography>
                                                    <Typography variant="body2" color="textSecondary">
                                                        Call gap flags moved to Calls Analytics.
                                                    </Typography>
                                                </Box>
                                                <ReportsIcon color="info" sx={{ fontSize: 40, ml: 2 }} />
                                            </Box>
                                            <Box mt={1}>
                                                <Chip 
                                                    label="Use Performance > Calls Analytics"
                                                    color="info"
                                                    size="small"
                                                />
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                {/* User Activity Metrics */}
                                <Grid item xs={12}>
                                    <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }}>
                                        User Activity
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Card>
                                        <CardContent>
                                            <Box display="flex" alignItems="center" justifyContent="between">
                                                <Box>
                                                    <Typography variant="h4" color={`${agentsSentTodayColor}.main`}>
                                                        {agentsSentToday}/{dashboardStats?.userActivity?.totalActiveAgents || 0}
                                                    </Typography>
                                                    <Typography variant="body2" color="textSecondary">
                                                        Agents Sent Today
                                                    </Typography>
                                                </Box>
                                                <PeopleIcon color={agentsSentTodayColor} sx={{ fontSize: 40, ml: 2 }} />
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                <Grid item xs={12} sm={6} md={3}>
                                    <Card>
                                        <CardContent>
                                            <Box display="flex" alignItems="center" justifyContent="between">
                                                <Box>
                                                    <Typography variant="h4" color="info.main">
                                                        {userActivitySummary?.activeRecently ?? 0}
                                                    </Typography>
                                                    <Typography variant="body2" color="textSecondary">
                                                        Active Recently
                                                    </Typography>
                                                </Box>
                                                <PeopleIcon color="info" sx={{ fontSize: 40, ml: 2 }} />
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                <Grid item xs={12} sm={6} md={3}>
                                    <Card>
                                        <CardContent>
                                            <Box display="flex" alignItems="center" justifyContent="between">
                                                <Box>
                                                    <Typography variant="h4" color="warning.main">
                                                        {userActivitySummary?.noActivityLast4Days ?? 0}
                                                    </Typography>
                                                    <Typography variant="body2" color="textSecondary">
                                                        No Activity 4+ Days
                                                    </Typography>
                                                </Box>
                                                <ReportsIcon color="warning" sx={{ fontSize: 40, ml: 2 }} />
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                <Grid item xs={12} sm={6} md={3}>
                                    <Card>
                                        <CardContent>
                                            <Box display="flex" alignItems="center" justifyContent="between">
                                                <Box>
                                                    <Typography variant="h4" color="error.main">
                                                        {userActivitySummary?.noActivityLast14Days ?? 0}
                                                    </Typography>
                                                    <Typography variant="body2" color="textSecondary">
                                                        No Activity 14+ Days
                                                    </Typography>
                                                </Box>
                                                <PeopleIcon color="error" sx={{ fontSize: 40, ml: 2 }} />
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                {/* Quick Actions */}
                                <Grid item xs={12}>
                                    <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }}>
                                        Quick Actions
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <Card 
                                        sx={{ 
                                            cursor: 'pointer',
                                            transition: 'all 0.3s ease',
                                            '&:hover': { 
                                                boxShadow: 6,
                                                transform: 'translateY(-4px)'
                                            }
                                        }}
                                        onClick={() => setSelectedView('users')}
                                    >
                                        <CardContent>
                                            <Box display="flex" alignItems="center" mb={1}>
                                                <PeopleIcon sx={{ mr: 1, color: 'primary.main' }} />
                                                <Typography variant="h6">
                                                    User Management
                                                </Typography>
                                            </Box>
                                            <Typography variant="body2">
                                                Manage agent and admin accounts
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <Card 
                                        sx={{ 
                                            cursor: 'pointer',
                                            transition: 'all 0.3s ease',
                                            '&:hover': { 
                                                boxShadow: 6,
                                                transform: 'translateY(-4px)'
                                            }
                                        }}
                                        onClick={() => setSelectedView('calls-performance-webhook')}
                                    >
                                        <CardContent>
                                            <Box display="flex" alignItems="center" mb={1}>
                                                <ReportsIcon sx={{ mr: 1, color: 'error.main' }} />
                                                <Typography variant="h6">
                                                    Call Performance
                                                </Typography>
                                            </Box>
                                            <Typography variant="body2">
                                                Monitor agent call gaps and performance
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <Card 
                                        sx={{ 
                                            cursor: 'pointer',
                                            transition: 'all 0.3s ease',
                                            '&:hover': { 
                                                boxShadow: 6,
                                                transform: 'translateY(-4px)'
                                            }
                                        }}
                                        onClick={() => setSelectedView('templates')}
                                    >
                                        <CardContent>
                                            <Box display="flex" alignItems="center" mb={1}>
                                                <TemplateIcon sx={{ mr: 1, color: 'warning.main' }} />
                                                <Typography variant="h6">
                                                    Templates
                                                </Typography>
                                            </Box>
                                            <Typography variant="body2">
                                                Create and manage email templates
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                {/* Send Offer card hidden for now - may be enabled in future */}
                                {/* <Grid item xs={12} sm={6} md={3}>
                                    <Card 
                                        sx={{ 
                                            cursor: 'pointer',
                                            transition: 'all 0.3s ease',
                                            '&:hover': { 
                                                boxShadow: 6,
                                                transform: 'translateY(-4px)'
                                            }
                                        }}
                                        onClick={() => setSelectedView('send-offer')}
                                    >
                                        <CardContent>
                                            <Box display="flex" alignItems="center" mb={1}>
                                                <EmailIcon sx={{ mr: 1, color: 'success.main' }} />
                                                <Typography variant="h6">
                                                    Send Offer
                                                </Typography>
                                            </Box>
                                            <Typography variant="body2">
                                                Create and send custom offers to customers
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid> */}
                            </Grid>
                        )}
                        
                        {/* Top Senders and Recent Activity Row */}
                        {(dashboardStats?.topPerformers || dashboardStats) && (
                            <Grid container spacing={3} sx={{ mt: 3 }}>
                                {dashboardStats?.topPerformers && (
                                            <Grid item xs={12} md={6}>
                                                <Typography variant="h6" gutterBottom color="primary">
                                                    Top Senders
                                                </Typography>
                                                <Card>
                                                <CardContent>
                                                    {dashboardStats.topPerformers.length > 0 ? (
                                                        dashboardStats.topPerformers.slice(0, 5).map((performer, index) => (
                                                            <Box key={performer.agentId} py={1} borderBottom={index < 4 ? 1 : 0} borderColor="divider">
                                                                <Typography variant="body2">
                                                                    <strong>{performer.agentName}</strong> - {performer.emailCount} emails sent
                                                                </Typography>
                                                                <Typography variant="caption" color="textSecondary">
                                                                    Today: {performer.todayCount} | This week: {performer.weeklyCount}
                                                                </Typography>
                                                            </Box>
                                                        ))
                                                    ) : (
                                                        <Typography variant="body2" color="textSecondary">
                                                            No performance data available
                                                        </Typography>
                                                    )}
                                                </CardContent>
                                            </Card>
                                            </Grid>
                                        )}
                                        
                                        {dashboardStats && (
                                        <Grid item xs={12} md={6}>
                                            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                                            <Typography variant="h6" gutterBottom color="primary">
                                                📝 Recent Activity
                                            </Typography>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() => setShowMoreRecentActivity((current) => !current)}
                                                >
                                                    {showMoreRecentActivity ? 'Show less' : 'Show last 15'}
                                                </Button>
                                            </Box>
                                            <Card>
                                                <CardContent>
                                                    {recentActivity.length > 0 ? (
                                                        recentActivity.slice(0, recentActivityLimit).map((activity, index, visibleItems) => (
                                                            <Box key={`${activity.timestamp}-${index}`} py={1} borderBottom={index < visibleItems.length - 1 ? 1 : 0} borderColor="divider">
                                                                <Typography variant="body2">
                                                                    <strong>{activity.user}</strong> {activity.action}
                                                                    {activity.sourceLabel && activity.sourceLabel !== 'Template' && (
                                                                        <Chip
                                                                            label={activity.sourceLabel}
                                                                            color="info"
                                                                            size="small"
                                                                            variant="outlined"
                                                                            sx={{ ml: 1, height: 20 }}
                                                                        />
                                                                    )}
                                                                </Typography>
                                                                <Typography variant="caption" color="textSecondary">
                                                                    {formatActivityTimestamp(activity.timestamp)}
                                                                </Typography>
                                                            </Box>
                                                        ))
                                                    ) : (
                                                        <Typography variant="body2" color="textSecondary">
                                                            No recent activity
                                                        </Typography>
                                                    )}
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                        )}
                            </Grid>
                        )}
                    </Box>
            );
        }

        const renderer = ADMIN_VIEW_RENDERERS[selectedView];
        return renderer ? renderer({
            escalationsAvailability,
            escalationsAvailabilityLoaded,
            onEscalationsAvailabilityChange
        }) : (
            <Paper sx={{ p: 3 }}>
                <Typography variant="h4" gutterBottom>
                    Page Not Found
                </Typography>
            </Paper>
        );
    };

    return renderView();
}

export default AdminDashboard;
